import React from 'react';
import { Row } from '@tanstack/react-table';
import { Document } from '@/hooks/useDocuments';
import { AttachmentRow } from './AttachmentRow';

type TableType = 'correspondence' | 'case-files' | 'all-files';

interface ExpandedAttachmentRowsProps {
  row: Row<Document>;
  slug: string;
  onDocumentClick: (documentGuid: string) => void;
  /** Table type determines column layout - correspondence has 10 cols, case-files has 7, all-files has 8 */
  tableType?: TableType;
}

/**
 * Reusable component for rendering expanded attachment rows in document tables.
 * Used in Correspondence, All Files, and other case files views.
 *
 * Returns actual <tr> elements for column alignment with parent rows.
 * Must be used with renderSubRows prop on DataTable (not renderSubComponent).
 */
export const ExpandedAttachmentRows: React.FC<ExpandedAttachmentRowsProps> = ({
  row,
  slug,
  onDocumentClick,
  tableType = 'correspondence',
}) => {
  const document = row.original;
  if (!document || !Array.isArray(document.attachments)) {
    return null;
  }

  const attachments = document.attachments;

  if (attachments.length === 0) {
    return null;
  }

  // Return actual <tr> elements for column alignment with parent rows
  return (
    <>
      {attachments.map((attachment, index) => (
        <AttachmentRow
          key={attachment.guid}
          attachment={attachment}
          slug={slug}
          parentDocumentGuid={document.id}
          onDocumentClick={onDocumentClick}
          isLast={index === attachments.length - 1}
          tableType={tableType}
        />
      ))}
    </>
  );
};

/**
 * Helper function to check if a row can expand (has attachments).
 * Use this with the getRowCanExpand prop on DataTable.
 */
export const canRowExpand = (row: Row<Document>): boolean => {
  return row.original.attachments && row.original.attachments.length > 0;
};
